import { Image } from "@/components/common/Image"
import { ContentLoading } from "@/components/content-loading"
import { FormSelect } from "@/components/Selector"
import { useCardDetails, useTopUpStore } from "@usher/pe-client-front-end-lib"
import { clsx } from "clsx"
import { ReactNode } from "react"
import { UseFormReturn } from "react-hook-form"
import { DetailsRow } from "./DetailsRow"

interface Props {
  formMethods: UseFormReturn<any>
}

export const CardDetails = ({ formMethods }: Props) => {
  const mainControl = formMethods.control
  const { categoryOptions, binLoading, supportText, binOptions, static_options, binDetails, hasValues } = useCardDetails(formMethods)
  const control = formMethods.control
  const setCardType = useTopUpStore((state) => state.setCardType)
  return (
    <div className="grid grid-cols-2 gap-2.5 order-card-details">
      <div className="col-span-2">
        <FormSelect
          label="Card Category"
          options={categoryOptions}
          control={control}
          name="card_category"
          required
          variant="contained"
          fullWidth
          onChange={(val) => {
            formMethods.setValue("card_bin", void 0)
            formMethods.setValue("type", void 0)
            formMethods.setValue("card_category", val)
            formMethods.setValue("card_currency", void 0)
            formMethods.setValue("card_type", "void 0")
          }}
        />
      </div>
      <FormSelect
        disabled={!hasValues.hasCategory}
        label="Card Option"
        onlyValue
        variant="contained"
        required
        fullWidth
        leftSection={(value) => {
          if (!value) return null
          return <Image className="w-5" src={value === "Virtual" ? "/card-red.svg" : "/card-blue.svg"} />
        }}
        control={formMethods.control}
        name="type"
        options={static_options.types}
        classNames={{ control: "!text-[12px]", list: "!text-[12px]" }}
        onChange={(option) => {
          setCardType(option.label)
        }}
      />
      <FormSelect
        disabled={!hasValues.hasType}
        label="Currency"
        options={static_options.curencies}
        control={control}
        name="card_currency"
        variant="outlined"
        required
        fullWidth
        classNames={{ control: "!text-[12px]", list: "!text-[12px]" }}
        onChange={() => {
          formMethods.setValue("card_bin", undefined)
        }}
      />
      <FormSelect
        required
        options={binOptions}
        variant="outlined"
        label="BIN"
        disabled={!hasValues.hasCardCurrency}
        control={mainControl}
        name="card_bin"
        className="col-span-2 mb-2"
        fullWidth
        // @ts-ignore
        renderOptions={(option, props, key) => {
          return (
            <div key={key} {...props} className={clsx(props.className, "leading-5")}>
              <span className="font-medium">{option.label}</span>
              {option.meta?.info && <span className="text-gray-500">{` Recommended use: ${option.meta.info}`}</span>}
            </div>
          ) as ReactNode
        }}
        getValueLabel={(option) => {
          if (option == null) return ""
          if (typeof option === "string") return option
          return (
            <span>
              {/*@ts-ignore*/}
              {option?.label} {(option?.meta ?? {})?.info}
            </span>
          )
        }}
      />
      <ContentLoading loading={binLoading} className="col-span-2">
        <div className="border bg-gray-100/50 px-4 py-3 col-span-2 mb-2">
          <div className="flex gap-2 mb-4">
            <div className="w-5 h-5 min-w-5 bg-blue-800 text-white grid place-items-center rounded-full font-bold">!</div>
            <strong className="text-sm">Card Details</strong>
          </div>
          <div className="flex flex-col gap-2.5 mb-3 border-b border-b-gray-200 pb-3">
            <DetailsRow title="Brand:">{binDetails?.card_brand || "N/A"}</DetailsRow>
            <DetailsRow title="Country:">{binDetails?.card_country || "N/A"}</DetailsRow>
            <DetailsRow title="Type:">{binDetails?.card_type || "N/A"}</DetailsRow>
            {binDetails?.card_currency && <DetailsRow title="Currency:">{binDetails?.card_currency}</DetailsRow>}
          </div>

          <div className="flex flex-col gap-2.5">
            {binDetails?.topup_fee && <DetailsRow title="Top Up Fee:">{binDetails?.topup_fee || 0}</DetailsRow>}
            {binDetails?.transaction_fee && <DetailsRow title="Transaction Fee:">{binDetails?.transaction_fee}</DetailsRow>}
            {binDetails?.fx && <DetailsRow title="FX Fee:">{binDetails?.fx}</DetailsRow>}
            {binDetails?.decline_fee && <DetailsRow title="Decline Fee:">{binDetails?.decline_fee}</DetailsRow>}
            {binDetails?.small_tr_fee && <DetailsRow title="Small Transaction Fee:">{binDetails?.small_tr_fee}</DetailsRow>}
            <DetailsRow title="3DS Support:">{binDetails?.["3ds_support"] || "Not Supported"}</DetailsRow>
            <DetailsRow title="Apple/Google Pay:">{supportText}</DetailsRow>
          </div>
          {binDetails?.info && <span className="block mt-3 text-blue-800 font-bold text-sm">Recommended use: {binDetails.info}</span>}
        </div>
      </ContentLoading>
    </div>
  )
}
